import { ArrowLeft, ArrowRight, DoubleLeftArrow, DoubleRightArrow } from 'features/compositions/assets/icons/icons';
import styles from './TablePagination.module.css';
import { useCompositionAdvancedOptionsContext } from 'features/compositions/context/CompositionAdvancedOptionsContext';
import { useCompositionsContext } from 'features/compositions/context/CompositionsContext';
import { useEffect } from 'react';

const TablePagination = ({ dark }) => {
    const { selectedComposition } = useCompositionsContext();
    const {
        page,
        setPage,
        limit,
        setLocalLimit,
        setLocalReverse,
        setLocalQuery,
        nextKey,
        prevKey,
        setPrevKey,
        setNextKey,
        query,
        reverse,
        primaryIndexName,
        setPrimaryIndexName,
        firstPageMax,
        setFirstPageMax
    } = useCompositionAdvancedOptionsContext();

    useEffect(() => {
        if (primaryIndexName) return;
        const idxName = selectedComposition?.structure?.indexes?.[0]?.name;
        if (!idxName) return;
        setPrimaryIndexName(idxName);
    }, [primaryIndexName, selectedComposition?.structure?.indexes, setPrimaryIndexName]);

    useEffect(() => {
        if (!primaryIndexName) return;
        if (!(page === 1 && !reverse)) return;
        if (firstPageMax !== null && firstPageMax !== undefined) return;

        const rows = selectedComposition?.rows || [];
        const scopeCount = Number(selectedComposition?.scope_count) || 0;
        const lim = scopeCount % limit;
        const remainder = lim ? lim : limit;
        const targetCount = remainder ? remainder : Math.min(limit, rows.length);

        const targetIndex = targetCount ? targetCount : 0; // arrays are 0-based
        if (rows[targetIndex] && rows[targetIndex][primaryIndexName] !== undefined && rows[targetIndex][primaryIndexName] !== null) {
            setFirstPageMax(rows[targetIndex][primaryIndexName]);
        }
    }, [page, reverse, selectedComposition, limit, primaryIndexName, firstPageMax, setFirstPageMax]);

    const total = Object.keys(query).length ?
        Number(selectedComposition?.count) || 0
        : Number(selectedComposition?.scope_count) || 0;
    const totalPages = Math.max(1, Math.ceil(total / (limit || 1)));

    const canPrev = page > 1 || prevKey;

    const canNext = page < totalPages || nextKey;


    const goPrev = () => {
        if (canPrev) {
            setPage(page - 1);
            // Always set localReverse to true when going back
            // This ensures API gets reverse ^ true, which gives us the previous page correctly
            // When reverse = false: API gets false ^ true = true (reverse order for going back)
            // When reverse = true: API gets true ^ true = false (normal order for going back in reverse mode)
            setLocalReverse(true);
            setLocalLimit(limit);
            
            // Calculate nextKey based on prevKey for future navigation
            if(!reverse) {
                setNextKey(Number(prevKey) ? Number(prevKey) + 1 : 1);
            } else {
                setNextKey(Number(prevKey) ? Number(prevKey) - 1 : 1);
            }
            
            // Set query based on reverse mode
            const idxName = primaryIndexName || selectedComposition?.structure?.indexes?.[0]?.name;
            if (idxName && prevKey) {
                setLocalQuery(prev => ({
                    ...prev,
                    [idxName]: !reverse ? {
                        min: null,
                        max: prevKey
                    } : {
                        min: prevKey,
                        max: null
                    }
                }));
            }
        }
    };
    const goNext = () => {
        if (canNext) {
            setPage(page + 1);
            // Always set localReverse to false when going forward
            // This ensures API gets reverse ^ false, which gives us the next page correctly
            // When reverse = false: API gets false ^ false = false (normal order for going forward)
            // When reverse = true: API gets true ^ false = true (reverse order for going forward in reverse mode)
            setLocalReverse(false);
            setLocalLimit(limit);
            
            // Calculate prevKey based on nextKey for future navigation
            setPrevKey((Number(nextKey) || 0) + (reverse ? 1 : -1));
            
            // Set query based on reverse mode
            const idxName = primaryIndexName || selectedComposition?.structure?.indexes?.[0]?.name;
            if (idxName && nextKey) {
                setLocalQuery(prev => ({
                    ...prev,
                    [idxName]: !reverse ? {
                        min: nextKey,
                        max: null
                    } : {
                        min: null,
                        max: nextKey
                    }
                }));
            }
        }
    };

    const goFirst = () => {
        if (canPrev) {
            setPrevKey("");
            setLocalLimit(limit);
            setPage(1);
            setLocalReverse(false);
            setLocalQuery({});
        }
    };
    const goLast = () => {
        if (canNext) {
            const lim = selectedComposition.scope_count % limit;
            setPage(totalPages);
            setLocalLimit(lim ? lim : limit);
            
            if (!reverse) {
                // Normal mode: go to last page
                // localReverse = true → API gets false ^ true = true (reverse order for last page)
                // API will return next_value → set as prevKey (for going back)
                setNextKey("");
                setLocalReverse(true);
                setLocalQuery({});
            } else {
                // Reverse mode: go to last page
                // We need prevKey for going back, so localReverse = true
                // API gets: reverse: true ^ true = false (normal order)
                // This gives us the first page in normal order, which is the last page in reverse mode
                // Empty query will get us the first page in normal order (last in reverse mode)
                setNextKey("");
                setLocalReverse(true);
                setLocalQuery({});
            }
        }
    };

    return (
        <div className={`${styles.table_pagination} ${dark ? styles.dark : ''}`}>
            <button className={styles.navigator_btn} onClick={goFirst} disabled={!canPrev}><DoubleLeftArrow /></button>
            <button className={styles.navigator_btn} onClick={goPrev} disabled={!canPrev}><ArrowLeft /></button>

            {!Object.keys(query).length && <div className={styles.pages}>
                <span>{page}</span> / <span>{totalPages}</span>
            </div>}

            <button className={styles.navigator_btn} onClick={goNext} disabled={!canNext}><ArrowRight /></button>
            <button className={styles.navigator_btn} onClick={goLast} disabled={!canNext}><DoubleRightArrow /></button>
        </div>
    );
}

export default TablePagination
